import React, { useState, useContext, useEffect } from 'react';
import { AuthContext, API } from '@/App';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { showToast } from '@/lib/custom-toast';
import {
  TrendingUp, Eye, EyeOff, Crown, Check, Zap, X,
  Target, MessageSquare, BarChart3, Clock, Mic, Gem, ChevronRight, Star,
  CreditCard, Share2, Bell, Shield, Globe, Smartphone, ChevronDown, ChevronUp,
  FileSpreadsheet, Users, Infinity as InfinityIcon
} from 'lucide-react';
import Footer from '@/components/Footer';
import GoogleOAuthButton from '@/components/GoogleOAuthButton';
import ForgotPasswordModal from '@/components/ForgotPasswordModal';
import { isAppMode } from '@/utils/pwa-utils';

// Componente Scroll Reveal con física de resorte (Emil Kowalski)
const RevealOnScroll = ({ children, className = '', delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const domRef = React.useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    const current = domRef.current;
    if (current) observer.observe(current);

    return () => {
      if (current) observer.unobserve(current);
    };
  }, []);

  return (
    <div
      ref={domRef}
      style={{
        transitionDuration: '650ms',
        transitionDelay: `${delay}ms`,
        transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)'
      }}
      className={`transition-all ${
        isVisible
          ? 'opacity-100 translate-y-0 scale-100'
          : 'opacity-0 translate-y-8 scale-[0.98]'
      } ${className}`}
    >
      {children}
    </div>
  );
};

const AuthPage = () => {
  const { login } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly'); // monthly or annual
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showComparisonTable, setShowComparisonTable] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [openFaqId, setOpenFaqId] = useState(null);
  const [pendingSubscription, setPendingSubscription] = useState(null);
  const [emailError, setEmailError] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: ''
  });

  // Detectar si está en modo PWA (standalone)
  const [isStandalone, setIsStandalone] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const isApp = isAppMode();
    setIsStandalone(isApp);
    setIsChecking(false);

    if (isApp) {
      // Si está en PWA instalada, asegurar que el modal de login esté abierto
      setShowAuthModal(true);
    }
  }, []);

  // Scroll a sección de precios si viene desde el footer o URL con ?pricing=true o hash
  useEffect(() => {
    if (searchParams.get('pricing') === 'true' || window.location.hash === '#pricing-section') {
      const scrollTimer = setTimeout(() => {
        const el = document.getElementById('pricing-section');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 150);
      return () => clearTimeout(scrollTimer);
    }
  }, [searchParams]);

  // Abrir modal con pill de registro si viene ?register=true o ?mode=register
  useEffect(() => {
    if (searchParams.get('register') === 'true' || searchParams.get('mode') === 'register') {
      setIsLogin(false);
      setEmailError('');
      setShowAuthModal(true);
    } else if (searchParams.get('login') === 'true' || searchParams.get('mode') === 'login') {
      setIsLogin(true);
      setEmailError('');
      setShowAuthModal(true);
    }
  }, [searchParams]);

  // Bloquear scroll de la página cuando algún modal esté abierto
  useEffect(() => {
    if (showAuthModal || showForgotPasswordModal) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [showAuthModal, showForgotPasswordModal]);

  const handleCloseAuthModal = () => {
    setShowAuthModal(false);
    setFormData({ username: '', email: '', password: '' });
    setEmailError('');
  };

  // Abre el modal guardando la intención de suscripción
  const handleOpenPlanModal = (plan) => {
    const sub = {
      plan: plan.name,
      priceId: billingCycle === 'monthly' ? plan.monthlyPriceId : plan.annualPriceId,
      cycle: billingCycle,
      price: billingCycle === 'monthly' ? plan.monthlyPrice : plan.annualPrice
    };
    try {
      localStorage.setItem('pending_subscription', JSON.stringify(sub));
      sessionStorage.setItem('pending_subscription', JSON.stringify(sub));
    } catch {}
    setPendingSubscription(sub);
    setIsLogin(false);
    setEmailError('');
    setShowAuthModal(true);
  };

  const redirectAfterAuth = async (token, user) => {
    await login(token, user);
    setShowAuthModal(false);
    setFormData({ username: '', email: '', password: '' });
    setEmailError('');
    // La pending_subscription queda en localStorage/sessionStorage para que el
    // banner del dashboard la detecte y ofrezca el pago al usuario.
    if (window.Capacitor) {
      window.location.href = '/dashboard';
    } else {
      navigate('/dashboard');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setEmailError('');

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(`${API}${endpoint}`, payload);

      if (!isLogin) {
        // Auto-login inmediato tras registro
        if (response.data.access_token) {
          showToast.success('¡Cuenta creada exitosamente!');
          await redirectAfterAuth(response.data.access_token, response.data.user);
        } else {
          // Backend no retornó token aún — fallback: iniciar sesión manual
          showToast.success('¡Cuenta creada! Inicia sesión para continuar.');
          setFormData({ username: '', email: formData.email, password: '' });
          setIsLogin(true);
        }
      } else {
        showToast.success('¡Bienvenido!');
        await redirectAfterAuth(response.data.access_token, response.data.user);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error en la autenticación';
      const isAlreadyRegistered =
        !isLogin && (
          errorMessage.toLowerCase().includes('registrado') ||
          errorMessage.toLowerCase().includes('already')
        );

      if (isAlreadyRegistered) {
        setEmailError('Este correo ya está registrado en PGastos.');
        showToast.error('Este correo ya está registrado. Te cambiamos a Iniciar Sesión.');
        setIsLogin(true);
      } else {
        showToast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const pillars = [
    {
      icon: Mic,
      title: '🎙️ Voz con Desglose de Artículos',
      description: 'Dicta tus compras en lenguaje natural. La IA separa cada artículo con su precio y detecta automáticamente la cuenta bancaria usada.',
      gradient: 'from-blue-500 to-indigo-600',
      tag: 'IA'
    },
    {
      icon: CreditCard,
      title: '💳 Tarjetas de Crédito y MSI',
      description: 'Registra compras a Meses Sin Intereses por tarjeta y registra tus pagos mensuales para llevar un control exacto de tus saldos.',
      gradient: 'from-emerald-500 to-teal-600',
      tag: 'Control'
    },
    {
      icon: Share2,
      title: '🤝 Deudas y Cobros con Liga SPEI',
      description: 'Maneja deudas con alguien más o deudas que tienen contigo. Comparte tu CLABE vía liga pública SPEI y registra o sube tus comprobantes de pago.',
      gradient: 'from-violet-500 to-purple-600',
      tag: 'Nuevo'
    },
    {
      icon: BarChart3,
      title: '📊 Analítica y Exportaciones',
      description: 'Radiografía financiera completa: ingresos, egresos y flujo neto. Exporta a Excel profesional, PDF formal o CSV cuando lo necesites.',
      gradient: 'from-orange-500 to-amber-600',
      tag: 'Pro'
    },
    {
      icon: Bell,
      title: '🔔 Recordatorios Inteligentes',
      description: 'Recordatorios de pago vinculados a tus tarjetas y deudas. Nunca pagues intereses por olvido.',
      gradient: 'from-pink-500 to-rose-600',
      tag: 'Gratis'
    },
    {
      icon: MessageSquare,
      title: '🤖 Chat IA Asesor Financiero',
      description: 'Asistente con contexto de tus finanzas reales. Pregunta lo que sea: ¿en qué gasto más? ¿cómo ahorro? ¿cuánto debo?',
      gradient: 'from-sky-500 to-cyan-600',
      tag: 'Premium'
    }
  ];

  const faqs = [
    {
      id: 1,
      q: '¿Es seguro usar PGastos?',
      a: 'Sí. No solicitamos contraseñas bancarias ni acceso directo a tus cuentas. Tú registras manualmente tus movimientos o por voz. Tus datos están cifrados y nunca se comparten con terceros.'
    },
    {
      id: 2,
      q: '¿Cómo funciona el dictado por voz con desglose?',
      a: 'Grabas en lenguaje natural lo que gastaste: "Compré leche en $32, café en $85 y fruta en $120 con la tarjeta Nu". La IA estructura automáticamente cada artículo con su precio y detecta la cuenta.'
    },
    {
      id: 3,
      q: '¿El plan Gratis expira algún día?',
      a: 'No. El plan Gratis es gratuito para siempre sin necesidad de ingresar tarjeta de crédito. Incluye transacciones ilimitadas, analítica completa, control de deudas y MSI, y 5 exportaciones al mes.'
    },
    {
      id: 4,
      q: '¿Puedo cancelar mi suscripción en cualquier momento?',
      a: 'Sí, con un solo clic desde tu perfil. Sin penalizaciones ni periodos de permanencia. Conservas acceso hasta el fin del periodo pagado.'
    },
    {
      id: 5,
      q: '¿Funciona en iPhone y Android?',
      a: 'Sí. Como PWA instalable desde el navegador en iOS y Android (sin ocupar espacio). Próximamente disponible en Google Play Store como app nativa.'
    }
  ];

  const plans = [
    {
      name: 'Gratis',
      icon: Zap,
      price: '$0',
      period: 'Para siempre',
      color: 'slate',
      features: [
        'Transacciones manuales ilimitadas',
        'Hasta 3 cuentas bancarias o tarjetas',
        'Analítica financiera completa',
        '5 exportaciones al mes (Excel/PDF/CSV)',
        'Control de deudas y compras a MSI',
        'Recordatorios de pago',
        'Dashboard con estadísticas',
        'Modo offline'
      ],
      cta: 'Comenzar Gratis',
      popular: false
    },
    {
      name: 'Premium',
      icon: Crown,
      monthlyPrice: '$79',
      monthlyRegular: '$99',
      monthlyPriceId: 'price_1TUYglJ42rbYbursKjX3zUVE',
      annualPrice: '$50',
      annualRegular: '$79',
      annualPriceId: 'price_1TUYglJ42rbYbursKjX3zUVE_annual',
      period: 'MXN/mes',
      color: 'blue',
      features: [
        '✨ Todo lo de Gratis +',
        '💳 Hasta 10 cuentas bancarias o tarjetas',
        '🎙️ 100 dictados por voz con desglose/mes',
        '📸 5 escaneos OCR de tickets/mes (prueba)',
        '📎 Comprobantes ilimitados en deudas',
        '🤖 Chat IA Asesor Financiero',
        '🎯 Metas de ahorro ilimitadas',
        '🔔 Notificaciones automáticas'
      ],
      cta: 'Obtener Oferta',
      popular: true
    },
    {
      name: 'Pro',
      icon: Gem,
      monthlyPrice: '$129',
      monthlyRegular: '$149',
      monthlyPriceId: 'price_pro_monthly',
      annualPrice: '$83',
      annualRegular: '$129',
      annualPriceId: 'price_pro_annual',
      period: 'MXN/mes',
      color: 'purple',
      features: [
        '💎 Todo lo de Premium +',
        '💳 Cuentas bancarias ILIMITADAS',
        '🎙️ Voz IA ILIMITADA',
        '📸 Escaneo OCR de tickets ILIMITADO',
        '📊 Exportaciones ILIMITADAS (Excel/PDF/CSV)',
        '🚀 Soporte prioritario VIP 24/7',
        '⚡ Acceso anticipado a nuevas funciones'
      ],
      cta: 'Obtener Oferta',
      popular: false,
      bestValue: true
    }
  ];

  const comparisonFeatures = [
    { name: 'Transacciones manuales', free: true, prem: true, pro: true },
    { name: 'Categorías personalizadas', free: true, prem: true, pro: true },
    { name: 'Dashboard con estadísticas', free: true, prem: true, pro: true },
    { name: 'Transacciones recurrentes', free: true, prem: true, pro: true },
    { name: 'Registro de inversiones', free: true, prem: true, pro: true },
    { name: 'Filtros temporales', free: true, prem: true, pro: true },
    { name: 'Cuentas bancarias', freeText: 'Hasta 3', premText: 'Hasta 10', proText: 'Ilimitadas' },
    { name: '🎙️ Transacciones por voz', free: false, premText: '100/mes', proText: '300/mes' },
    { name: '🤖 IA Financiera + Chat', free: false, prem: true, pro: true },
    { name: '🎯 Objetivos financieros', free: false, prem: true, pro: true },
    { name: '📈 Análisis avanzados', free: false, prem: false, pro: true },
    { name: '📊 Exportar Excel/CSV/PDF', free: false, prem: false, pro: true },
    { name: '🚫 Sin publicidad', free: false, prem: true, pro: true },
    { name: '🚀 Soporte prioritario', free: false, prem: false, pro: true }
  ];

  // Mostrar loading mientras se detecta la plataforma
  if (isChecking) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-2 border-stone-200 border-t-indigo-600 mb-3"></div>
          <p className="text-stone-400 text-sm">Cargando...</p>
        </div>
      </div>
    );
  }

  // Si está en PWA standalone, mostrar solo el formulario de login/registro limpio
  if (isStandalone) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
        <Card className="relative bg-white rounded-2xl w-full max-w-md shadow-2xl">
          <div className="p-8">
            {/* Logo */}
            <div className="text-center mb-8">
              <img
                src="/icon-192x192.png"
                alt="PGastos"
                className="h-16 w-auto mx-auto mb-4"
              />
              <h2 className="text-3xl font-bold text-slate-900 mb-2">
                {isLogin ? 'Iniciar Sesión' : 'Crear Cuenta'}
              </h2>
              <p className="text-slate-600">
                {isLogin ? '¡Bienvenido de vuelta!' : 'Comienza gratis en segundos'}
              </p>
            </div>

            {/* Google OAuth Button */}
            <GoogleOAuthButton
              text={isLogin ? "Iniciar sesión con Google" : "Registrarse con Google"}
            />

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-slate-500">O continúa con</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <Label htmlFor="username">Nombre de Usuario</Label>
                  <Input
                    id="username"
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required={!isLogin}
                    className="mt-1"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({ ...formData, email: e.target.value });
                    if (emailError) setEmailError('');
                  }}
                  required
                  className={`mt-1 ${emailError ? 'border-red-500 focus:ring-red-500' : ''}`}
                />
                {emailError && (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1 font-medium">
                    ⚠️ {emailError}
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label htmlFor="password">Contraseña</Label>
                  {isLogin && (
                    <button
                      type="button"
                      onClick={() => setShowForgotPasswordModal(true)}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      ¿Olvidaste tu contraseña?
                    </button>
                  )}
                </div>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-600 hover:text-slate-900"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold active:scale-95 transition-all duration-150 ease-spring shadow-sm cursor-pointer"
                size="lg"
                disabled={loading}
              >
                {loading ? 'Procesando...' : (isLogin ? 'Iniciar Sesión' : 'Crear Cuenta Gratis')}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  setIsLogin(!isLogin);
                  setEmailError('');
                }}
                className="text-indigo-600 hover:text-indigo-700 font-medium text-sm active:scale-95 transition-all duration-150 ease-spring cursor-pointer"
              >
                {isLogin ? '¿No tienes cuenta? Regístrate' : '¿Ya tienes cuenta? Inicia sesión'}
              </button>
            </div>
          </div>
        </Card>

        {/* Forgot Password Modal */}
        <ForgotPasswordModal
          isOpen={showForgotPasswordModal}
          onClose={() => setShowForgotPasswordModal(false)}
        />
      </div>
    );
  }

  // Landing page completa para navegador web
  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-stone-50/90 backdrop-blur-sm border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5">
          <div className="flex items-center justify-between gap-2">
            <Link to="/" className="flex items-center gap-2 sm:gap-2.5 hover:opacity-75 transition-opacity duration-150 flex-shrink-0">
              <img
                src="/icon-192x192.png"
                alt="PGastos"
                className="h-7 sm:h-8 w-auto"
              />
              <span className="text-base sm:text-lg font-bold text-stone-900 tracking-tight">PGastos</span>
            </Link>

            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setIsLogin(true);
                  setShowAuthModal(true);
                }}
                className="text-xs sm:text-sm font-medium text-stone-600 hover:text-stone-900 px-3 py-1.5 rounded-lg hover:bg-stone-100 active:scale-95 transition-all duration-150 ease-spring cursor-pointer"
              >
                Entrar
              </button>
              <button
                onClick={() => {
                  setIsLogin(false);
                  setShowAuthModal(true);
                }}
                className="text-xs sm:text-sm font-semibold bg-stone-900 hover:bg-stone-800 text-white px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-xl active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring shrink-0 cursor-pointer shadow-sm"
              >
                <span className="hidden sm:inline">Comenzar gratis</span>
                <span className="sm:hidden">Gratis</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden w-full max-w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-24">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 text-center lg:text-left">
              {/* Badge de contexto */}
              <div className="animate-spring-in inline-flex items-center gap-2 px-3.5 py-1.5 bg-white border border-stone-200/90 rounded-full mb-6 shadow-xs hover:scale-[1.02] transition-transform duration-150 ease-spring">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                <span className="text-xs font-semibold text-stone-700">
                  Control de gastos y finanzas para México
                </span>
              </div>

              <h1 className="animate-spring-in [animation-delay:80ms] text-4xl sm:text-5xl lg:text-6xl font-black text-stone-900 mb-5 leading-[1.08] tracking-tight">
                Tus finanzas personales, por fin bajo control.
              </h1>

              <p className="animate-spring-in [animation-delay:150ms] text-base sm:text-lg text-stone-600 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed font-normal">
                Registra gastos en segundos por dictado de voz, gestiona tus compras a Meses Sin Intereses y comparte ligas SPEI para cobrar o saldar deudas con comprobantes.
              </p>

              <div className="animate-spring-in [animation-delay:220ms] flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <button
                  onClick={() => {
                    setIsLogin(false);
                    setShowAuthModal(true);
                  }}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm sm:text-base px-7 py-3.5 sm:py-4 rounded-xl active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring shadow-md shadow-indigo-100 flex items-center justify-center gap-2 group cursor-pointer"
                >
                  <span>Empezar gratis</span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-150 ease-spring" />
                </button>
                <button
                  onClick={() => {
                    const el = document.getElementById('pricing-section');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="text-stone-700 hover:text-stone-900 font-semibold text-sm sm:text-base px-6 sm:px-8 py-3.5 sm:py-4 rounded-xl border border-stone-200 bg-white hover:bg-stone-50 active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring shadow-xs cursor-pointer"
                >
                  Ver planes
                </button>
              </div>

              {/* Puntos de Confianza */}
              <div className="animate-spring-in [animation-delay:300ms] mt-8 pt-6 border-t border-stone-200/80 flex flex-wrap items-center gap-x-5 gap-y-2 justify-center lg:justify-start text-xs text-stone-500 font-medium">
                <div className="flex items-center gap-1.5 hover:text-stone-800 transition-colors">
                  <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span>Gratis para siempre</span>
                </div>
                <div className="flex items-center gap-1.5 hover:text-stone-800 transition-colors">
                  <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span>Sin tarjeta de crédito</span>
                </div>
                <div className="flex items-center gap-1.5 hover:text-stone-800 transition-colors">
                  <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span>Web, PWA y Android</span>
                </div>
              </div>
            </div>

            {/* Right: Mockup Integrado de Producto */}
            <div className="lg:col-span-5 w-full">
              <div className="relative mx-auto max-w-md lg:max-w-none">
                <div className="animate-spring-right bg-white rounded-3xl border border-stone-200 shadow-xl shadow-stone-200/60 overflow-hidden hover:shadow-2xl transition-all duration-300 ease-spring">
                  {/* Top Bar de la App */}
                  <div className="px-5 py-3.5 bg-stone-50/80 border-b border-stone-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-stone-300"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-stone-300"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-stone-300"></div>
                      <span className="ml-2 text-xs font-bold text-stone-700 tracking-tight">PGastos Dashboard</span>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-100">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                      </span>
                      En vivo
                    </span>
                  </div>

                  <div className="p-5 sm:p-6 space-y-4">
                    {/* Balance General */}
                    <div className="flex items-end justify-between pb-3.5 border-b border-stone-100 group">
                      <div>
                        <p className="text-xs font-medium text-stone-400">Balance neto disponible</p>
                        <p className="text-2xl sm:text-3xl font-black text-stone-900 tracking-tight mt-0.5 group-hover:text-indigo-950 transition-colors">
                          $24,850.00 <span className="text-xs font-semibold text-stone-400">MXN</span>
                        </p>
                      </div>
                      <span className="inline-flex items-center text-xs font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-lg hover:scale-105 transition-transform ease-spring">
                        +$4,200 <span className="font-normal text-[10px] ml-1 text-emerald-600">este mes</span>
                      </span>
                    </div>

                    {/* Módulo: Dictado por voz */}
                    <div className="p-3.5 rounded-xl bg-stone-50 border border-stone-200/70 space-y-2 hover:scale-[1.015] hover:shadow-xs transition-all duration-150 ease-spring cursor-default">
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2 text-indigo-700 font-bold">
                          <div className="w-6 h-6 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center">
                            <Mic className="w-3.5 h-3.5" />
                          </div>
                          <span>Dictado por voz procesado</span>
                        </div>
                        {/* Audio equalizer wave micro-animation */}
                        <div className="flex items-center gap-1.5">
                          <div className="flex items-end gap-0.5 h-3.5">
                            <span className="w-0.5 bg-indigo-500 rounded-full animate-wave-1"></span>
                            <span className="w-0.5 bg-indigo-400 rounded-full animate-wave-2"></span>
                            <span className="w-0.5 bg-indigo-600 rounded-full animate-wave-3"></span>
                            <span className="w-0.5 bg-indigo-500 rounded-full animate-wave-2"></span>
                          </div>
                          <span className="text-[10px] text-stone-400 font-medium">hace 5 min</span>
                        </div>
                      </div>
                      <div className="bg-white p-2.5 rounded-lg border border-stone-100 space-y-1.5 text-xs">
                        <div className="flex justify-between items-center text-stone-700">
                          <span className="font-medium">Despensa semanal</span>
                          <span className="font-bold text-stone-900">$480.00</span>
                        </div>
                        <div className="flex justify-between items-center text-stone-700">
                          <span className="font-medium">Capuchino vainilla</span>
                          <span className="font-bold text-stone-900">$65.00</span>
                        </div>
                      </div>
                    </div>

                    {/* Módulo: Tarjetas y MSI */}
                    <div className="p-3.5 rounded-xl bg-white border border-stone-200/80 shadow-xs hover:scale-[1.015] hover:shadow-xs transition-all duration-150 ease-spring cursor-default">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                            <CreditCard className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="text-xs font-bold text-stone-900">Laptop Workstation · BBVA</p>
                            <p className="text-[11px] text-stone-500">Pago 4 de 12 mensualidades</p>
                          </div>
                        </div>
                        <span className="text-xs font-black text-stone-900">
                          $1,499<span className="text-[10px] font-normal text-stone-400">/mes</span>
                        </span>
                      </div>
                      <div className="w-full bg-stone-100 rounded-full h-1.5 overflow-hidden">
                        <div className="bg-emerald-500 h-1.5 rounded-full transition-all duration-700 ease-out" style={{ width: '33%' }}></div>
                      </div>
                    </div>

                    {/* Módulo: Deuda SPEI */}
                    <div className="p-3.5 rounded-xl bg-white border border-stone-200/80 shadow-xs flex items-center justify-between hover:scale-[1.015] hover:shadow-xs transition-all duration-150 ease-spring cursor-default">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-700 flex items-center justify-center shrink-0">
                          <Share2 className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-stone-900">Cobro SPEI · Deuda compartida</p>
                          <p className="text-[11px] text-stone-500">Liga pública · Comprobante recibido</p>
                        </div>
                      </div>
                      <span className="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200/60 px-2 py-0.5 rounded-md hover:scale-105 transition-transform ease-spring">
                        Saldada ✓
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features / Pillars Section */}
      <section className="py-16 sm:py-24 bg-white border-y border-stone-200">
        <RevealOnScroll className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-xs font-semibold uppercase tracking-widest text-stone-400 mb-3">Funcionalidades</p>
            <h2 className="text-2xl sm:text-4xl font-black text-stone-900 mb-4 tracking-tight">
              Todo para llevar tus cuentas
            </h2>
            <p className="text-sm sm:text-base text-stone-500 font-medium">
              Sin fricción para registrar, sin sorpresas para pagar.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-stone-200 border border-stone-200 rounded-2xl overflow-hidden">
            {pillars.map((pillar, index) => {
              const IconComp = pillar.icon;
              return (
                <div
                  key={index}
                  className="p-6 sm:p-7 bg-white hover:bg-stone-50/90 transition-all duration-200 ease-spring group cursor-default relative hover:z-10"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 rounded-xl bg-stone-900 flex items-center justify-center group-hover:scale-110 group-hover:bg-indigo-600 transition-all duration-200 ease-spring shadow-sm">
                      <IconComp className="w-5 h-5 text-white" />
                    </div>
                    {pillar.tag && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-stone-100 text-stone-600 border border-stone-200 group-hover:border-indigo-200 group-hover:text-indigo-600 group-hover:bg-indigo-50/50 transition-colors duration-200">
                        {pillar.tag}
                      </span>
                    )}
                  </div>
                  <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors duration-150">
                    {pillar.title.replace(/^[\p{Emoji}\u200d\uFE0F]+\s*/u, '')}
                  </h3>
                  <p className="text-sm text-stone-500 leading-relaxed">
                    {pillar.description}
                  </p>
                </div>
              );
            })}
          </div>
        </RevealOnScroll>
      </section>

      {/* Pricing Section */}
      <section id="pricing-section" className="py-16 sm:py-24 bg-stone-50 scroll-mt-10">
        <RevealOnScroll className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-stone-400 mb-3">Precios</p>
            <h2 className="text-2xl sm:text-4xl font-black text-stone-900 mb-3 tracking-tight">
              Simple y sin letra pequeña
            </h2>
            <p className="text-sm sm:text-base text-stone-500 mb-8 max-w-md mx-auto font-medium">
              Empieza gratis. Actualiza cuando lo necesites.
            </p>

            {/* Billing Toggle */}
            <div className="inline-flex items-center p-1 bg-stone-100 rounded-xl border border-stone-200 shadow-inner">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-5 sm:px-6 py-2 rounded-lg font-bold text-xs sm:text-sm active:scale-95 transition-all duration-200 ease-spring cursor-pointer ${
                  billingCycle === 'monthly'
                    ? 'bg-white text-stone-900 shadow-sm'
                    : 'text-stone-500 hover:text-stone-800'
                }`}
              >
                Mensual
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`px-5 sm:px-6 py-2 rounded-lg font-bold text-xs sm:text-sm active:scale-95 transition-all duration-200 ease-spring flex items-center gap-1.5 cursor-pointer ${
                  billingCycle === 'annual'
                    ? 'bg-white text-stone-900 shadow-sm'
                    : 'text-stone-500 hover:text-stone-800'
                }`}
              >
                Anual
                <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full text-[10px] font-bold">
                  -37%
                </span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 sm:gap-4 md:gap-6 max-w-5xl mx-auto items-stretch pt-4">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`p-3 sm:p-5 md:p-6 rounded-2xl relative flex flex-col justify-between bg-white transition-all duration-200 ease-spring hover:-translate-y-2 hover:shadow-2xl group cursor-default ${
                  plan.popular
                    ? 'border-2 border-indigo-600 shadow-xl shadow-indigo-100/50 sm:-translate-y-1'
                    : 'border border-stone-200 shadow-sm hover:shadow-md'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 sm:-top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap z-10">
                    <span className="bg-indigo-600 text-white text-[9px] sm:text-[11px] font-bold uppercase tracking-wider px-2.5 sm:px-3.5 py-0.5 sm:py-1 rounded-full shadow-sm">
                      <span className="sm:hidden">Popular</span>
                      <span className="hidden sm:inline">Más popular</span>
                    </span>
                  </div>
                )}
                {plan.bestValue && (
                  <div className="absolute -top-3 sm:-top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap z-10">
                    <span className="bg-stone-800 text-white text-[9px] sm:text-[11px] font-bold uppercase tracking-wider px-2.5 sm:px-3.5 py-0.5 sm:py-1 rounded-full shadow-sm">
                      <span className="sm:hidden">Mejor</span>
                      <span className="hidden sm:inline">Mejor valor</span>
                    </span>
                  </div>
                )}

                <div>
                  <div className="text-center mb-3 sm:mb-4 pt-1 sm:pt-2">
                    <div className={`inline-flex items-center justify-center w-9 h-9 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-xl sm:rounded-2xl mb-1.5 sm:mb-3 group-hover:scale-110 transition-transform duration-200 ease-spring ${
                      plan.popular
                        ? 'bg-indigo-50 text-indigo-600'
                        : plan.bestValue
                        ? 'bg-purple-50 text-purple-600'
                        : 'bg-stone-100 text-stone-700'
                    }`}>
                      <plan.icon className="w-4 h-4 sm:w-6 sm:h-6 md:w-7 md:h-7" />
                    </div>

                    <h3 className="text-xs sm:text-xl md:text-2xl font-bold text-stone-900 mb-1">
                      {plan.name}
                    </h3>

                    <div className="flex flex-col items-center justify-center min-h-[42px] sm:min-h-[52px]">
                      <div className="h-4 sm:h-5 flex items-center justify-center mb-0.5">
                        {plan.name !== 'Gratis' ? (
                          <span className="text-[9px] sm:text-xs text-stone-400 line-through font-medium">
                            {billingCycle === 'monthly' ? `${plan.monthlyRegular} MXN` : `${plan.annualRegular} MXN`}
                          </span>
                        ) : (
                          <span className="text-[9px] sm:text-xs text-transparent select-none font-medium">
                            -
                          </span>
                        )}
                      </div>
                      <div className="flex items-baseline justify-center gap-0.5 sm:gap-1">
                        <span className="text-xl sm:text-3xl md:text-5xl font-black text-stone-900 tracking-tight">
                          {plan.name === 'Gratis'
                            ? '$0'
                            : billingCycle === 'monthly'
                            ? plan.monthlyPrice
                            : plan.annualPrice}
                        </span>
                        <span className="text-[9px] sm:text-xs text-stone-400 font-medium">
                          {plan.name === 'Gratis' ? (
                            <>
                              <span className="hidden sm:inline">para </span>siempre
                            </>
                          ) : (
                            'MXN/mes'
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => {
                    if (plan.name === 'Gratis') {
                      setIsLogin(false);
                      setPendingSubscription(null);
                      localStorage.removeItem('pending_subscription');
                      sessionStorage.removeItem('pending_subscription');
                      setEmailError('');
                      setShowAuthModal(true);
                    } else {
                      handleOpenPlanModal(plan);
                    }
                  }}
                  className={`w-full py-2 sm:py-3 md:py-3.5 px-2 sm:px-4 font-bold text-[11px] sm:text-sm md:text-base rounded-xl active:scale-95 hover:scale-[1.01] transition-all duration-150 ease-spring cursor-pointer ${
                    plan.popular
                      ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-md shadow-indigo-200/50'
                      : plan.name === 'Gratis'
                      ? 'bg-stone-100 hover:bg-stone-200 text-stone-800 border border-stone-200/80'
                      : 'bg-stone-900 hover:bg-stone-800 text-white shadow-sm'
                  }`}
                >
                  {plan.name === 'Gratis' ? (
                    <>
                      <span>Empezar</span>
                      <span className="hidden sm:inline"> gratis</span>
                    </>
                  ) : (
                    <>
                      <span>Obtener</span>
                      <span className="hidden sm:inline"> plan</span>
                    </>
                  )}
                </Button>
              </div>
            ))}
          </div>

            <div className="mt-10 text-center">
            <button
              onClick={() => setShowComparisonTable(!showComparisonTable)}
              className="inline-flex items-center gap-2 px-5 py-3 bg-white hover:bg-stone-50 text-stone-700 font-semibold text-sm rounded-xl border border-stone-200 hover:border-stone-300 active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring cursor-pointer shadow-xs"
            >
              <span>{showComparisonTable ? 'Ocultar comparación' : 'Ver comparación completa'}</span>
              <div className={`transition-transform duration-200 ease-spring ${showComparisonTable ? 'rotate-180 text-indigo-600' : 'text-stone-400'}`}>
                <ChevronDown className="w-4 h-4" />
              </div>
            </button>
          </div>

          {/* Inline Comparison Table */}
          {showComparisonTable && (
            <div className="mt-8 bg-white rounded-2xl p-4 sm:p-8 border border-stone-200 max-w-6xl mx-auto animate-spring-in">
              <h3 className="text-base sm:text-xl font-bold text-stone-900 mb-6 text-center tracking-tight">
                Comparación de planes
              </h3>

              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-stone-200">
                      <th className="text-left py-4 px-4 text-stone-500 font-semibold text-sm align-bottom">Funcionalidad</th>
                      <th className="text-center py-4 px-4 text-stone-700 font-semibold">
                        <div className="text-xs text-stone-400 font-medium">Plan</div>
                        <div className="text-sm font-bold text-stone-900">Gratis</div>
                        <div className="text-lg font-black text-stone-900 mt-0.5">$0</div>
                        <div className="text-[11px] text-stone-400 font-normal">Para siempre</div>
                      </th>
                      <th className="text-center py-4 px-4 text-indigo-700 font-semibold bg-indigo-50/50 rounded-t-lg">
                        <div className="text-[10px] text-indigo-500 font-bold uppercase tracking-wider">Más Popular</div>
                        <div className="text-sm font-bold text-stone-900">Premium</div>
                        <div className="text-lg font-black text-stone-900 mt-0.5">
                          {billingCycle === 'monthly' ? '$79' : '$50'} <span className="text-xs font-normal text-stone-400">MXN/mes</span>
                        </div>
                      </th>
                      <th className="text-center py-4 px-4 text-violet-700 font-semibold bg-violet-50/50 rounded-t-lg">
                        <div className="text-[10px] text-violet-500 font-bold uppercase tracking-wider">Mejor Valor</div>
                        <div className="text-sm font-bold text-stone-900">Pro</div>
                        <div className="text-lg font-black text-stone-900 mt-0.5">
                          {billingCycle === 'monthly' ? '$129' : '$83'} <span className="text-xs font-normal text-stone-400">MXN/mes</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100 text-sm">
                    {comparisonFeatures.map((feat, idx) => (
                      <tr key={idx} className="hover:bg-stone-50/60 transition-colors">
                        <td className="py-3 px-4 text-stone-700 font-medium">{feat.name.replace(/^[\p{Emoji}\u200d\uFE0F]+\s*/u, '')}</td>
                        <td className="text-center py-3 px-4">
                          {feat.freeText ? (
                            <span className="text-xs font-semibold text-stone-700">{feat.freeText}</span>
                          ) : feat.free ? (
                            <Check className="w-4 h-4 text-emerald-500 mx-auto" />
                          ) : (
                            <span className="w-4 h-4 text-stone-200 mx-auto block text-center">&mdash;</span>
                          )}
                        </td>
                        <td className="text-center py-3 px-4 bg-indigo-50/30">
                          {feat.premText ? (
                            <span className="text-xs font-bold text-indigo-600">{feat.premText}</span>
                          ) : feat.prem ? (
                            <Check className="w-4 h-4 text-emerald-500 mx-auto" />
                          ) : (
                            <span className="text-stone-200 block text-center">&mdash;</span>
                          )}
                        </td>
                        <td className="text-center py-3 px-4 bg-violet-50/30">
                          {feat.proText ? (
                            <span className="text-xs font-bold text-violet-600">{feat.proText}</span>
                          ) : feat.pro ? (
                            <Check className="w-4 h-4 text-emerald-500 mx-auto" />
                          ) : (
                            <span className="text-stone-200 block text-center">&mdash;</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-stone-200">
                      <td className="py-6 px-4 text-stone-700 font-bold text-sm align-middle">
                        Elegir plan
                      </td>
                      <td className="py-6 px-4 text-center">
                        <div className="mb-2">
                          <span className="text-xl font-black text-stone-900">$0</span>
                          <span className="text-xs text-stone-400 block font-medium">para siempre</span>
                        </div>
                        <Button
                          onClick={() => {
                            setIsLogin(false);
                            setPendingSubscription(null);
                            localStorage.removeItem('pending_subscription');
                            sessionStorage.removeItem('pending_subscription');
                            setEmailError('');
                            setShowAuthModal(true);
                          }}
                          className="w-full py-2.5 px-3 bg-stone-100 hover:bg-stone-200 text-stone-800 font-bold text-xs rounded-xl active:scale-95 transition-all duration-150 ease-spring border border-stone-200 cursor-pointer"
                        >
                          Empezar gratis
                        </Button>
                      </td>
                      <td className="py-6 px-4 text-center bg-indigo-50/40 rounded-b-xl">
                        <div className="mb-2">
                          <span className="text-xl font-black text-stone-900">
                            {billingCycle === 'monthly' ? '$79' : '$50'}
                          </span>
                          <span className="text-xs text-stone-400 block font-medium">MXN/mes</span>
                        </div>
                        <Button
                          onClick={() => {
                            const prem = plans.find(p => p.name === 'Premium');
                            if (prem) handleOpenPlanModal(prem);
                          }}
                          className="w-full py-2.5 px-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl active:scale-95 transition-all duration-150 ease-spring shadow-md shadow-indigo-100 cursor-pointer"
                        >
                          Obtener plan
                        </Button>
                      </td>
                      <td className="py-6 px-4 text-center bg-violet-50/40 rounded-b-xl">
                        <div className="mb-2">
                          <span className="text-xl font-black text-stone-900">
                            {billingCycle === 'monthly' ? '$129' : '$83'}
                          </span>
                          <span className="text-xs text-stone-400 block font-medium">MXN/mes</span>
                        </div>
                        <Button
                          onClick={() => {
                            const pro = plans.find(p => p.name === 'Pro');
                            if (pro) handleOpenPlanModal(pro);
                          }}
                          className="w-full py-2.5 px-3 bg-stone-900 hover:bg-stone-800 text-white font-bold text-xs rounded-xl active:scale-95 transition-all duration-150 ease-spring shadow-sm cursor-pointer"
                        >
                          Obtener plan
                        </Button>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Mobile Comparison Cards */}
              <div className="md:hidden space-y-2">
                <div className="grid grid-cols-3 gap-2 text-center mb-3 pb-3 border-b border-stone-200 sticky top-14 z-20 bg-white/95 backdrop-blur-md p-2 rounded-xl">
                  <div className="text-xs font-bold text-stone-700">
                    <div>Gratis</div>
                    <div className="text-sm font-black text-stone-900">$0</div>
                  </div>
                  <div className="text-xs font-bold text-indigo-600">
                    <div>Premium</div>
                    <div className="text-sm font-black text-stone-900">{billingCycle === 'monthly' ? '$79' : '$50'}</div>
                  </div>
                  <div className="text-xs font-bold text-violet-600">
                    <div>Pro</div>
                    <div className="text-sm font-black text-stone-900">{billingCycle === 'monthly' ? '$129' : '$83'}</div>
                  </div>
                </div>
                {comparisonFeatures.map((feat, idx) => (
                  <div key={idx} className="bg-stone-50 rounded-xl p-3 border border-stone-100">
                    <p className="font-semibold text-stone-800 text-xs mb-2">{feat.name.replace(/^[\p{Emoji}\u200d\uFE0F]+\s*/u, '')}</p>
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                      <div className="flex items-center justify-center py-1.5 px-1 bg-white border border-stone-200 rounded-lg min-h-[32px]">
                        {feat.freeText ? (
                          <span className="font-semibold text-stone-700 text-[11px]">{feat.freeText}</span>
                        ) : feat.free ? (
                          <Check className="w-3.5 h-3.5 text-emerald-500" />
                        ) : (
                          <span className="text-stone-300 text-sm">&mdash;</span>
                        )}
                      </div>
                      <div className="flex items-center justify-center py-1.5 px-1 bg-indigo-50 border border-indigo-100 rounded-lg min-h-[32px]">
                        {feat.premText ? (
                          <span className="font-bold text-indigo-600 text-[11px]">{feat.premText}</span>
                        ) : feat.prem ? (
                          <Check className="w-3.5 h-3.5 text-emerald-500" />
                        ) : (
                          <span className="text-stone-300 text-sm">&mdash;</span>
                        )}
                      </div>
                      <div className="flex items-center justify-center py-1.5 px-1 bg-violet-50 border border-violet-100 rounded-lg min-h-[32px]">
                        {feat.proText ? (
                          <span className="font-bold text-violet-600 text-[11px]">{feat.proText}</span>
                        ) : feat.pro ? (
                          <Check className="w-3.5 h-3.5 text-emerald-500" />
                        ) : (
                          <span className="text-stone-300 text-sm">&mdash;</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Mobile Bottom CTA row */}
                <div className="grid grid-cols-3 gap-2 pt-4 border-t border-stone-200 mt-4">
                  <div className="text-center">
                    <div className="text-xs font-black text-stone-900 mb-1">$0</div>
                    <Button
                      onClick={() => {
                        setIsLogin(false);
                        setPendingSubscription(null);
                        localStorage.removeItem('pending_subscription');
                        sessionStorage.removeItem('pending_subscription');
                        setEmailError('');
                        setShowAuthModal(true);
                      }}
                      className="w-full py-2 px-1 bg-stone-100 hover:bg-stone-200 text-stone-800 font-bold text-[10px] rounded-lg active:scale-95 transition-all"
                    >
                      Gratis
                    </Button>
                  </div>
                  <div className="text-center">
                    <div className="text-xs font-black text-stone-900 mb-1">
                      {billingCycle === 'monthly' ? '$79' : '$50'}<span className="text-[9px] text-stone-400 font-normal">/m</span>
                    </div>
                    <Button
                      onClick={() => {
                        const premPlan = plans.find(p => p.name === 'Premium');
                        if (premPlan) handleOpenPlanModal(premPlan);
                      }}
                      className="w-full py-2 px-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-[10px] rounded-lg active:scale-95 transition-all shadow-sm"
                    >
                      Premium
                    </Button>
                  </div>
                  <div className="text-center">
                    <div className="text-xs font-black text-stone-900 mb-1">
                      {billingCycle === 'monthly' ? '$129' : '$83'}<span className="text-[9px] text-stone-400 font-normal">/m</span>
                    </div>
                    <Button
                      onClick={() => {
                        const proPlan = plans.find(p => p.name === 'Pro');
                        if (proPlan) handleOpenPlanModal(proPlan);
                      }}
                      className="w-full py-2 px-1 bg-stone-900 hover:bg-stone-800 text-white font-bold text-[10px] rounded-lg active:scale-95 transition-all shadow-sm"
                    >
                      Pro
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </RevealOnScroll>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-white border-t border-stone-200">
        <RevealOnScroll className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-stone-400 mb-3">Preguntas</p>
            <h2 className="text-2xl sm:text-3xl font-black text-stone-900 tracking-tight">
              Lo que nos preguntan seguido
            </h2>
          </div>

          <div className="divide-y divide-stone-200">
            {faqs.map((faq) => {
              const isOpen = openFaqId === faq.id;
              return (
                <div key={faq.id}>
                  <button
                    onClick={() => setOpenFaqId(isOpen ? null : faq.id)}
                    className="w-full text-left py-4 flex items-start justify-between gap-4 group active:scale-[0.99] transition-transform duration-100 ease-spring cursor-pointer"
                  >
                    <span className="text-sm sm:text-base font-semibold text-stone-900 group-hover:text-indigo-600 transition-colors duration-150">{faq.q}</span>
                    <div className={`shrink-0 mt-0.5 transition-transform duration-200 ease-spring ${isOpen ? 'rotate-180 text-indigo-600' : 'text-stone-400'}`}>
                      <ChevronDown className="w-4 h-4" />
                    </div>
                  </button>
                  {isOpen && (
                    <div className="pb-5 text-sm text-stone-500 leading-relaxed animate-spring-in">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </RevealOnScroll>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-stone-900">
        <RevealOnScroll className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-4xl font-black text-white mb-3 tracking-tight">
            Empieza hoy. Es gratis.
          </h2>
          <p className="text-sm sm:text-base text-stone-400 mb-8 max-w-md mx-auto font-medium">
            Sin tarjeta de crédito. Sin compromisos. Cancela cuando quieras.
          </p>
          <button
            onClick={() => {
              setIsLogin(false);
              setPendingSubscription(null);
              localStorage.removeItem('pending_subscription');
              sessionStorage.removeItem('pending_subscription');
              setEmailError('');
              setShowAuthModal(true);
            }}
            className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm sm:text-base px-8 py-4 rounded-2xl active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring shadow-lg shadow-indigo-900/50 cursor-pointer"
          >
            Crear cuenta gratis
          </button>
        </RevealOnScroll>
      </section>

      {/* Footer */}
      <Footer />

      {/* Auth Modal */}
      {showAuthModal && (
        <div
          className="fixed inset-0 bg-stone-950/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={handleCloseAuthModal}
        >
          <Card
            className="relative bg-white rounded-2xl w-full max-w-md my-8 shadow-2xl border border-stone-200 animate-spring-in"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleCloseAuthModal}
              className="absolute top-4 right-4 p-1.5 hover:bg-stone-100 rounded-lg text-stone-400 hover:text-stone-700 active:scale-90 transition-all duration-100 cursor-pointer"
            >
              <X className="w-4 h-4 text-stone-500" />
            </button>

            <div className="p-7">
              {/* Logo + Tabs */}
              <div className="text-center mb-6">
                <img src="/icon-192x192.png" alt="PGastos" className="h-9 w-auto mx-auto mb-4" />

                {/* Pills tabs */}
                <div className="inline-flex bg-stone-100 rounded-xl p-1 w-full border border-stone-200/80">
                  <button
                    onClick={() => {
                      setIsLogin(true);
                      setEmailError('');
                    }}
                    className={`flex-1 py-2 text-sm font-semibold rounded-lg active:scale-95 transition-all duration-150 ease-spring cursor-pointer ${
                      isLogin
                        ? 'bg-white text-stone-900 shadow-sm'
                        : 'text-stone-500 hover:text-stone-700'
                    }`}
                  >
                    Iniciar sesión
                  </button>
                  <button
                    onClick={() => {
                      setIsLogin(false);
                      setEmailError('');
                    }}
                    className={`flex-1 py-2 text-sm font-semibold rounded-lg active:scale-95 transition-all duration-150 ease-spring cursor-pointer ${
                      !isLogin
                        ? 'bg-white text-stone-900 shadow-sm'
                        : 'text-stone-500 hover:text-stone-700'
                    }`}
                  >
                    Crear cuenta
                  </button>
                </div>
              </div>

              {/* Pending Subscription Chip */}
              {pendingSubscription && (
                <div className="mb-5 p-3 bg-indigo-50 border border-indigo-200/80 rounded-xl flex items-center justify-between text-xs">
                  <span className="text-indigo-800 font-medium">Plan seleccionado:</span>
                  <span className="font-bold text-indigo-950 bg-white px-2.5 py-1 rounded-lg border border-indigo-100 shadow-xs uppercase tracking-wide text-[10px]">
                    {pendingSubscription.tier} · {pendingSubscription.cycle === 'yearly' ? 'Anual' : 'Mensual'}
                  </span>
                </div>
              )}

              {/* Google OAuth Button */}
              <GoogleOAuthButton
                text={isLogin ? 'Continuar con Google' : 'Registrarse con Google'}
              />

              {/* Divider */}
              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-stone-200"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-3 bg-white text-stone-400">o con tu correo</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {!isLogin && (
                  <div>
                    <Label htmlFor="username" className="text-sm font-medium text-stone-700">Nombre</Label>
                    <Input
                      id="username"
                      type="text"
                      placeholder="Tu nombre"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      required={!isLogin}
                      className="mt-1 h-10 border-stone-200 focus:border-indigo-600 focus:ring-indigo-600"
                    />
                  </div>
                )}

                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-stone-700">Correo electrónico</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@correo.com"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                      if (emailError) setEmailError('');
                    }}
                    required
                    className={`mt-1 h-10 border-stone-200 focus:border-indigo-600 focus:ring-indigo-600 ${emailError ? 'border-red-500 focus:ring-red-500' : ''}`}
                  />
                  {emailError && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1 font-medium">
                      ⚠️ {emailError}
                    </p>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="password" className="text-sm font-medium text-stone-700">Contraseña</Label>
                    {isLogin && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowAuthModal(false);
                          setShowForgotPasswordModal(true);
                        }}
                        className="text-xs text-indigo-600 hover:text-indigo-700 font-medium active:scale-95 transition-all duration-150 ease-spring cursor-pointer"
                      >
                        ¿Olvidaste tu contraseña?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder={isLogin ? 'Tu contraseña' : 'Crea una contraseña segura'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      className="h-10 pr-10 border-stone-200 focus:border-indigo-600 focus:ring-indigo-600"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 cursor-pointer"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>

                  {/* Indicador de fortaleza — solo en registro */}
                  {!isLogin && formData.password.length > 0 && (() => {
                    const pwd = formData.password;
                    const rules = [
                      { label: 'Mínimo 8 caracteres', ok: pwd.length >= 8 },
                      { label: 'Una mayúscula', ok: /[A-Z]/.test(pwd) },
                      { label: 'Un número', ok: /[0-9]/.test(pwd) },
                      { label: 'Un símbolo (!@#$...)', ok: /[^A-Za-z0-9]/.test(pwd) },
                    ];
                    const passed = rules.filter(r => r.ok).length;
                    const strength = passed <= 1 ? 'Débil' : passed === 2 ? 'Regular' : passed === 3 ? 'Buena' : 'Fuerte';
                    const colors = ['bg-red-400', 'bg-orange-400', 'bg-yellow-400', 'bg-green-500'];
                    return (
                      <div className="mt-2 space-y-1.5">
                        <div className="flex gap-1">
                          {[0,1,2,3].map(i => (
                            <div key={i} className={`h-1 flex-1 rounded-full transition-all ${ i < passed ? colors[passed - 1] : 'bg-stone-200'}`} />
                          ))}
                        </div>
                        <p className="text-[11px] text-stone-500 font-medium">{strength}</p>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                          {rules.map((rule, i) => (
                            <span key={i} className={`flex items-center gap-1 text-[11px] ${ rule.ok ? 'text-green-600' : 'text-stone-400'}`}>
                              <Check className="w-3 h-3 flex-shrink-0" />
                              {rule.label}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <Button
                  type="submit"
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold h-10 rounded-xl active:scale-95 transition-all duration-150 ease-spring shadow-sm cursor-pointer"
                  disabled={loading}
                >
                  {loading
                    ? 'Procesando...'
                    : isLogin
                    ? 'Iniciar sesión'
                    : 'Crear cuenta'}
                </Button>
              </form>

              {/* Badges de confianza */}
              <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-center gap-4 flex-wrap">
                <span className="flex items-center gap-1 text-[11px] text-slate-400">
                  <Shield className="w-3.5 h-3.5 text-slate-400" />
                  SSL 256-bit
                </span>
                <span className="flex items-center gap-1 text-[11px] text-slate-400">
                  <Check className="w-3.5 h-3.5 text-slate-400" />
                  Sin spam
                </span>
                <span className="flex items-center gap-1 text-[11px] text-slate-400">
                  <Check className="w-3.5 h-3.5 text-slate-400" />
                  Cancela cuando quieras
                </span>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        isOpen={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
      />
    </div>
  );
};

export default AuthPage;
